import React, { useMemo, useState } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "campaign-builder/ui/datepicker.css";
// eslint-disable-next-line import/no-unresolved
import "@oppizi-ecosystem/meeting/webcomponent";
// eslint-disable-next-line import/no-unresolved
import "@oppizi-ecosystem/meeting/styles.css";
import { CalendarDays, PhoneCall, X } from "lucide-react";
import CONFIG from "config/config";
import { isProduction } from "utils";
import { cn } from "lib/utils";
import { Popover, PopoverTrigger, PopoverContent, Sheet, SheetBody } from "campaign-builder/ui";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
import { parseScheduleDate, toScheduleDate, type AreaScheduleEntry } from "campaign-builder/state/h2hSchedule";

interface DistributionDatePickerProps {
  schedule: AreaScheduleEntry[];
  /** Bulk-add calendar days to the schedule (range / week / month selection). */
  onAddDates: (dates: string[]) => void;
  /** Bulk-remove calendar days from the schedule. */
  onRemoveDates: (dates: string[]) => void;
  /** Earliest selectable day — the OADS minimum lead time (T3). */
  minDate: Date;
  cypressId?: string;
}

// Class react-datepicker applies to the scheduled days (styled red, like a
// selected day) so multi-date selection reads at a glance in the inline calendar.
const SCHEDULED_DAY_CLASS = "react-datepicker__day--scheduled";
// Pending range start — visually distinct so the user sees the anchor while
// choosing the end of the range.
const RANGE_ANCHOR_CLASS = "react-datepicker__day--range-anchor";
const MAX_DAYS_AHEAD = 360;
// HUB-445: above this many selected days, surface the "book a call" nudge —
// very large campaigns are better planned with a specialist. Threshold TBD
// with product; keep it a named constant so it's a one-line change.
const LARGE_CAMPAIGN_DAY_THRESHOLD = 15;

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Every selectable day in [from, to] (inclusive, both clamped to bounds). */
const eligibleDaysBetween = (from: Date, to: Date, minDate: Date, maxDate: Date): string[] => {
  const first = startOfDay(from < to ? from : to);
  const last = startOfDay(from < to ? to : from);
  const floor = startOfDay(minDate);
  const ceil = startOfDay(maxDate);
  const days: string[] = [];
  for (let day = new Date(first); day <= last; day.setDate(day.getDate() + 1)) {
    if (day >= floor && day <= ceil) days.push(toScheduleDate(day));
  }
  return days;
};

// Inline calendar trigger for per-area scheduling (AC28). The pill shows the
// selected-date count; opening it reveals an inline month calendar with
// airline-style range selection (HUB-445): click a start day, click an end day
// and every day in between is scheduled; clicking an already-selected day
// unselects just that day (so exceptions like "skip Sundays" stay easy). Week
// rows and the current month can be toggled in one click. react-datepicker 4.x
// has no native multi-select, so dates are tracked via highlightDates.
export const DistributionDatePicker = ({
  schedule,
  onAddDates,
  onRemoveDates,
  minDate,
  cypressId,
}: DistributionDatePickerProps) => {
  const [open, setOpen] = useState(false);
  const [rangeAnchor, setRangeAnchor] = useState<string | null>(null);
  // Month currently shown in the calendar — drives the "Select entire month" shortcut.
  const [viewedMonth, setViewedMonth] = useState<Date>(() => startOfDay(minDate));
  const [bookCallOpen, setBookCallOpen] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);

  const { user } = useCampaignData();
  const { country } = useClientConfig();

  const count = schedule.length;
  const label = count === 0 ? "Select a distribution date" : `${count} date${count === 1 ? "" : "s"} selected`;

  const maxDate = new Date(minDate);
  maxDate.setDate(maxDate.getDate() + MAX_DAYS_AHEAD);

  const scheduledSet = useMemo(() => new Set(schedule.map((entry) => entry.date)), [schedule]);

  // Stable array reference unless the schedule changes — react-datepicker would
  // otherwise re-render the calendar on every parent (context) re-render.
  const scheduledDates = useMemo(
    () => schedule.map((entry) => parseScheduleDate(entry.date)).filter((d): d is Date => d !== null),
    [schedule]
  );
  const anchorDates = useMemo(() => {
    const anchor = rangeAnchor ? parseScheduleDate(rangeAnchor) : null;
    return anchor ? [anchor] : [];
  }, [rangeAnchor]);

  const handleDayClick = (date: Date) => {
    const day = toScheduleDate(date);
    if (scheduledSet.has(day)) {
      // Clicking inside an existing selection carves that day out.
      onRemoveDates([day]);
      if (rangeAnchor === day) setRangeAnchor(null);
      return;
    }
    if (rangeAnchor === null) {
      onAddDates([day]);
      setRangeAnchor(day);
      return;
    }
    const anchor = parseScheduleDate(rangeAnchor);
    if (!anchor) {
      onAddDates([day]);
      setRangeAnchor(day);
      return;
    }
    onAddDates(eligibleDaysBetween(anchor, date, minDate, maxDate));
    setRangeAnchor(null);
  };

  /** Toggle a set of days: all already selected → unselect them; otherwise add the missing ones. */
  const toggleDays = (days: string[]) => {
    if (days.length === 0) return;
    const missing = days.filter((day) => !scheduledSet.has(day));
    if (missing.length === 0) onRemoveDates(days);
    else onAddDates(missing);
    setRangeAnchor(null);
  };

  const handleWeekSelect = (firstDayOfWeek: Date) => {
    const weekEnd = new Date(firstDayOfWeek);
    weekEnd.setDate(weekEnd.getDate() + 6);
    toggleDays(eligibleDaysBetween(firstDayOfWeek, weekEnd, minDate, maxDate));
  };

  const monthDays = useMemo(() => {
    const first = new Date(viewedMonth.getFullYear(), viewedMonth.getMonth(), 1);
    const last = new Date(viewedMonth.getFullYear(), viewedMonth.getMonth() + 1, 0);
    return eligibleDaysBetween(first, last, minDate, maxDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedMonth, minDate.getTime(), maxDate.getTime()]);
  const monthFullySelected = monthDays.length > 0 && monthDays.every((day) => scheduledSet.has(day));

  const showLargeCampaignNudge = count >= LARGE_CAMPAIGN_DAY_THRESHOLD && !nudgeDismissed;

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          // Drop a half-picked range on close so reopening later can't silently
          // complete it and schedule unintended dates.
          if (!nextOpen) setRangeAnchor(null);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            data-cypress-id={cypressId}
            className={cn(
              "flex h-9 items-center gap-2 self-start rounded-md border border-solid bg-background px-3 text-sm font-medium text-foreground",
              "focus:outline-none focus-visible:border-slate-400 dark:focus-visible:border-slate-500 focus-visible:ring-1 focus-visible:ring-slate-200 dark:focus-visible:ring-slate-700",
              open ? "border-slate-400 dark:border-slate-500" : "border-border"
            )}
          >
            <CalendarDays className="size-4 text-muted-foreground" strokeWidth={1.75} />
            {label}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="z-[1350] w-auto border-none bg-transparent p-0 shadow-none">
          <div className="flex flex-col gap-2 rounded-lg border border-solid border-border bg-background p-3 shadow-md">
            <p
              className="m-0 max-w-[280px] text-xs leading-4 text-muted-foreground"
              data-cypress-id={`${cypressId}-help`}
            >
              Pick a start and an end date to select the whole range. Click any selected day to unselect it. Use a week
              number or the button below to toggle a full week or month.
            </p>
            <ReactDatePicker
              inline
              selected={null}
              // Without this, react-datepicker auto-highlights a pre-selected day
              // (the minDate/lead-time floor) as `--keyboard-selected`, which our
              // shared cb-datepicker styles red — making an unscheduled day look picked.
              disabledKeyboardNavigation
              minDate={minDate}
              maxDate={maxDate}
              shouldCloseOnSelect={false}
              calendarClassName="cb-datepicker"
              highlightDates={[{ [SCHEDULED_DAY_CLASS]: scheduledDates }, { [RANGE_ANCHOR_CLASS]: anchorDates }]}
              onChange={() => undefined}
              onSelect={(date) => date && handleDayClick(date)}
              showWeekNumbers
              onWeekSelect={(firstDayOfWeek) => handleWeekSelect(firstDayOfWeek)}
              onMonthChange={(month) => setViewedMonth(startOfDay(month))}
            />
            <button
              type="button"
              onClick={() => toggleDays(monthDays)}
              disabled={monthDays.length === 0}
              title={
                monthFullySelected
                  ? "Unselect every available day in the displayed month"
                  : "Select every available day in the displayed month"
              }
              data-cypress-id={`${cypressId}-select-month`}
              className={cn(
                "h-8 rounded-md border border-solid border-border bg-background px-3 text-xs font-medium text-foreground transition-colors",
                "enabled:hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              )}
            >
              {monthFullySelected ? "Unselect entire month" : "Select entire month"}
            </button>
            {showLargeCampaignNudge && (
              <div
                className="flex max-w-[280px] flex-col gap-2 rounded-md border border-solid border-border bg-muted/60 p-3"
                data-cypress-id={`${cypressId}-large-campaign-nudge`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="m-0 text-xs leading-4 text-foreground">
                    Planning a large-scale campaign? Book a call with our team for custom planning.
                  </p>
                  <button
                    type="button"
                    onClick={() => setNudgeDismissed(true)}
                    aria-label="Dismiss"
                    data-cypress-id={`${cypressId}-large-campaign-nudge-dismiss`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setBookCallOpen(true);
                  }}
                  data-cypress-id={`${cypressId}-large-campaign-book-call`}
                  className="flex h-8 items-center justify-center gap-1.5 self-start rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground transition-opacity hover:opacity-90"
                >
                  <PhoneCall className="size-3.5" />
                  Book a call
                </button>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <Sheet
        open={bookCallOpen}
        onClose={() => setBookCallOpen(false)}
        aria-label="Book a planning call"
        className="max-w-[600px]"
      >
        <div className="flex items-center gap-3 border-b border-solid border-border p-6">
          <h2 className="text-lg font-semibold" data-cypress-id={`${cypressId}-book-call-title`}>
            Book a planning call
          </h2>
        </div>
        <SheetBody>
          {bookCallOpen && (
            <oppizi-meeting
              first-name={user?.firstName ?? ""}
              last-name={user?.lastName ?? ""}
              email={user?.email ?? ""}
              country={country?.code}
              redirect-url={`${CONFIG.PLATFORM_BASE_URL}/dashboard/home`}
              ui-mode="embedded"
              is-prod={isProduction()}
              data-cypress-id={`${cypressId}-book-call-embed`}
            />
          )}
        </SheetBody>
      </Sheet>
    </>
  );
};
